import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PaginationService } from "ngx-pagination";
import { Observable, Subject } from "rxjs";
import { distinctUntilChanged, map, takeUntil } from "rxjs/operators";

import {
  SharedModule,
  LanguageCode,
  DataService,
  DataTableFilterCollection,
  FilterPresetService,
  BulkActionMenuComponent,
  DataTableComponentConfig,
  DataTableConfig,
  DataTable2SearchComponent,
  DataTableCustomComponentService,
  LocalStorageService,
  DataTableLocationId,
  DataTable2ColumnComponent,
  DataTableCustomFieldColumnComponent,
} from "@bb-vendure/admin-ui/core";
import ExpandingDivComponent from "./components/expand_div/expand.component";

@Component({
  selector: "custom-data-table",
  templateUrl: "data-table2.component.html",
  styleUrls: ["data-table2.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PaginationService, FilterPresetService],
  imports: [SharedModule],
  standalone: true,
})
export class DataTable2Component<T>
  implements AfterContentInit, OnChanges, OnDestroy
{
  @Input() id: DataTableLocationId;
  @Input() items: T[];
  @Input() itemsPerPage: number;
  @Input() currentPage: number;
  @Input() totalItems: number;
  @Input() emptyStateLabel: string;
  @Input() filters: DataTableFilterCollection;
  @Input() activeIndex = -1;
  @Input() expandedRowIndex: number | null = null;
  @Output() pageChange = new EventEmitter<number>();
  @Output() itemsPerPageChange = new EventEmitter<number>();

  @ContentChildren(DataTable2ColumnComponent) columns: QueryList<
    DataTable2ColumnComponent<T>
  >;
  @ContentChildren(DataTableCustomFieldColumnComponent)
  customFieldColumns: QueryList<DataTableCustomFieldColumnComponent<T>>;
  @ContentChild(DataTable2SearchComponent)
  searchComponent: DataTable2SearchComponent;
  @ContentChild(BulkActionMenuComponent)
  bulkActionMenuComponent: BulkActionMenuComponent;
  @ContentChild("vdrDt2CustomSearch") customSearchTemplate: TemplateRef<any>;
  @ContentChildren(TemplateRef) templateRefs: QueryList<TemplateRef<any>>;
  @ContentChild(ExpandingDivComponent)
  expandingDivComponent: ExpandingDivComponent;

  injector = inject(Injector);
  route = inject(ActivatedRoute);
  filterPresetService = inject(FilterPresetService);
  dataTableCustomComponentService = inject(DataTableCustomComponentService);
  protected customComponents = new Map<
    string,
    { config: DataTableComponentConfig; injector: Injector }
  >();

  rowTemplate: TemplateRef<any>;
  currentStart: number;
  currentEnd: number;

  // This is used to apply a `user-select: none` CSS rule to the table,
  // which allows shift-click multi-row selection
  disableSelect = false;
  showSearchFilterRow = false;

  protected uiLanguage$: Observable<LanguageCode>;
  protected destroy$ = new Subject<void>();

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    protected localStorageService: LocalStorageService,
    protected dataService: DataService
  ) {
    this.uiLanguage$ = this.dataService.client
      .uiState()
      .stream$.pipe(map(({ uiState }) => uiState.language));
  }

  get selectionManager() {
    return this.bulkActionMenuComponent?.selectionManager;
  }

  get allColumns() {
    return [...(this.columns ?? []), ...(this.customFieldColumns ?? [])];
  }

  get visibleSortedColumns() {
    return this.sortedColumns.filter((c) => c.visible);
  }

  get sortedColumns() {
    const columns = this.allColumns;
    const dataTableConfig = this.getDataTableConfig();
    for (const [id, index] of Object.entries(dataTableConfig[this.id].order)) {
      const column = columns.find((c) => c.id === id);
      const currentIndex = columns.findIndex((c) => c.id === id);
      if (currentIndex !== -1 && column) {
        columns.splice(currentIndex, 1);
        columns.splice(index, 0, column);
      }
    }
    return columns;
  }

  private shiftDownHandler = (event: KeyboardEvent) => {
    if (event.shiftKey && !this.disableSelect) {
      this.disableSelect = true;
      this.changeDetectorRef.markForCheck();
    }
  };

  private shiftUpHandler = (event: KeyboardEvent) => {
    if (this.disableSelect) {
      this.disableSelect = false;
      this.changeDetectorRef.markForCheck();
    }
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items) {
      this.currentStart = this.itemsPerPage * (this.currentPage - 1);
      this.currentEnd = this.currentStart + changes.items.currentValue?.length;
      this.selectionManager?.setCurrentItems(this.items);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.selectionManager) {
      document.removeEventListener("keydown", this.shiftDownHandler);
      document.removeEventListener("keyup", this.shiftUpHandler);
    }
  }

  ngAfterContentInit(): void {
    this.rowTemplate = this.templateRefs.last;
    const dataTableConfig = this.getDataTableConfig();
    if (!this.id) {
      console.warn(`No id was assigned to the data table component`);
    }
    const updateColumnVisibility = () => {
      dataTableConfig[this.id].visibility = this.allColumns
        .filter(
          (c) =>
            (c.visible && c.hiddenByDefault) ||
            (!c.visible && !c.hiddenByDefault)
        )
        .map((c) => c.id);
      this.localStorageService.set("dataTableConfig", dataTableConfig);
    };

    this.allColumns.forEach((column) => {
      if (dataTableConfig?.[this.id]?.visibility.includes(column.id)) {
        column.setVisibility(column.hiddenByDefault);
      }
      column.onColumnChange(updateColumnVisibility);
      const config =
        this.dataTableCustomComponentService.getCustomComponentsFor(
          this.id,
          column.id
        );
      if (config) {
        const injector = Injector.create({
          parent: this.injector,
          providers: config.providers ?? [],
        });
        this.customComponents.set(column.id, { config, injector });
      }
    });

    if (this.selectionManager) {
      document.addEventListener("keydown", this.shiftDownHandler, {
        passive: true,
      });
      document.addEventListener("keyup", this.shiftUpHandler, {
        passive: true,
      });
      this.bulkActionMenuComponent.onClearSelection(() => {
        this.changeDetectorRef.markForCheck();
      });
      this.selectionManager.setCurrentItems(this.items);
    }
    this.showSearchFilterRow =
      !!this.filters?.activeFilters.length ||
      (dataTableConfig?.[this.id]?.showSearchFilterRow ?? false);
    this.columns.changes.subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });

    this.selectionManager?.selectionChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.changeDetectorRef.markForCheck());

    if (this.selectionManager) {
      this.dataService.client
        .userStatus()
        .mapStream(({ userStatus }) => userStatus.activeChannelId)
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(() => {
          this.selectionManager?.clearSelection();
        });
    }
  }

  onColumnReorder(event: {
    column: DataTable2ColumnComponent<any>;
    newIndex: number;
  }) {
    const naturalIndex = this.allColumns.findIndex(
      (c) => c.id === event.column.id
    );
    const dataTableConfig = this.getDataTableConfig();
    if (naturalIndex === event.newIndex) {
      delete dataTableConfig[this.id].order[event.column.id];
    } else {
      dataTableConfig[this.id].order[event.column.id] = event.newIndex;
    }
    this.localStorageService.set("dataTableConfig", dataTableConfig);
  }

  onColumnsReset() {
    const dataTableConfig = this.getDataTableConfig();
    dataTableConfig[this.id].order = {};
    dataTableConfig[this.id].visibility = [];
    this.localStorageService.set("dataTableConfig", dataTableConfig);
  }

  toggleSearchFilterRow() {
    this.showSearchFilterRow = !this.showSearchFilterRow;
    const dataTableConfig = this.getDataTableConfig();
    dataTableConfig[this.id].showSearchFilterRow = this.showSearchFilterRow;
    this.localStorageService.set("dataTableConfig", dataTableConfig);
  }

  trackByFn(index: number, item: any) {
    if ((item as any).id != null) {
      return (item as any).id;
    } else {
      return index;
    }
  }



  onToggleAllClick() {
    this.selectionManager?.toggleSelectAll();
  }

  onItemSelect(item: T, event: MouseEvent) {
    this.selectionManager?.toggleSelection(item, event);
  }

  // onRowClick(index: number, event: MouseEvent): void {
  //   event.stopPropagation();
  //   if(this.expandingDivComponent === undefined) return;
  //   this.expandedRowIndex = this.expandedRowIndex === index ? null : index;
  // }

  protected getDataTableConfig(): DataTableConfig {
    const dataTableConfig =
      this.localStorageService.get("dataTableConfig") ?? {};
    if (!dataTableConfig[this.id]) {
      dataTableConfig[this.id] = {
        visibility: [],
        order: {},
        showSearchFilterRow: false,
        filterPresets: [],
      };
    }
    return dataTableConfig;
  }
}
